import type { StrategyItem } from '../api/strategyApi';
import { monthlyBudgetOf } from './strategyBudget';

export type ChannelBucket = 'paid' | 'organic';

export interface ChannelShare {
  key: string;
  label: string;
  bucket: ChannelBucket;
  monthly: number;
  /** Share of the total monthly budget, 0–100. */
  pct: number;
}

export interface ChannelMix {
  channels: ChannelShare[];
  totalMonthly: number;
  paidPct: number;
  organicPct: number;
  /** Approved strategies that carry a budget, i.e. what the mix is built from. */
  fundedStrategies: StrategyItem[];
}

const CHANNELS: Record<string, { label: string; bucket: ChannelBucket }> = {
  google_ads: { label: 'Search ads', bucket: 'paid' },
  meta_ads: { label: 'Social ads', bucket: 'paid' },
  seo_content: { label: 'SEO & content', bucket: 'organic' },
  nextdoor: { label: 'Nextdoor & community', bucket: 'organic' },
  direct_mail: { label: 'Direct mail', bucket: 'organic' },
};

/** Operational fixes cost staff time, not channel spend. */
const NON_SPEND_CHANNELS = new Set(['operational']);

function describe(key: string): { label: string; bucket: ChannelBucket } {
  return (
    CHANNELS[key] ?? {
      label: key.replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase()),
      bucket: 'organic',
    }
  );
}

/**
 * Split each approved strategy's recommended monthly budget evenly across the
 * spend channels it names, and total by channel. The result is the mix the
 * active strategy actually implies — nothing is assumed beyond that.
 */
export function computeChannelMix(
  strategies: StrategyItem[],
  approvedIndexes: number[],
): ChannelMix {
  const totals = new Map<string, number>();
  const fundedStrategies: StrategyItem[] = [];

  for (const i of approvedIndexes) {
    const s = strategies[i];
    if (!s) continue;
    const budget = monthlyBudgetOf(s);
    const spendChannels = (s.primary_channels ?? []).filter((c) => !NON_SPEND_CHANNELS.has(c));
    if (budget <= 0 || spendChannels.length === 0) continue;
    fundedStrategies.push(s);
    const share = budget / spendChannels.length;
    for (const c of spendChannels) totals.set(c, (totals.get(c) ?? 0) + share);
  }

  const totalMonthly = [...totals.values()].reduce((a, b) => a + b, 0);
  const channels: ChannelShare[] = [...totals.entries()]
    .map(([key, monthly]) => ({
      key,
      ...describe(key),
      monthly,
      pct: totalMonthly > 0 ? (monthly / totalMonthly) * 100 : 0,
    }))
    .sort((a, b) => b.monthly - a.monthly);

  const paidPct = channels.filter((c) => c.bucket === 'paid').reduce((a, c) => a + c.pct, 0);
  return {
    channels,
    totalMonthly,
    paidPct,
    organicPct: totalMonthly > 0 ? 100 - paidPct : 0,
    fundedStrategies,
  };
}
